// One ledger covers all visible renderers. It has no timer or animation loop:
// callers tick it with the display's RAF timestamp before doing optional work.
const DEFAULT_INTERVAL_MS = 1000 / 60;
const MAX_INTERVAL_SAMPLES = 20;
const DEADLINE_EPSILON_MS = 1e-6;
const validDuration = value => Number.isFinite(value) && value >= 0;
const boundedInterval = value => Number.isFinite(value) && value >= 4 && value <= 40;

export function createRenderBudgetLedger({ frameIntervalMs = DEFAULT_INTERVAL_MS } = {}) {
  return {
    intervalMs: boundedInterval(frameIntervalMs) ? frameIntervalMs : DEFAULT_INTERVAL_MS,
    intervalSamples: [],
    owners: new Set(),
    nextOwner: 0,
    nextTicket: 0,
    clockEpoch: 0,
    frame: null,
  };
}

const sharedLedger = createRenderBudgetLedger();

function hasPending(owner) {
  return owner.pendingLatest || owner.pendingProgress;
}

function isEligible(owner, ledger) {
  return !owner.disposed && owner.visible && hasPending(owner) && !owner.inFlight &&
    ledger.frame.time - owner.lastTick <= 2 * ledger.intervalMs + DEADLINE_EPSILON_MS;
}

function interactiveFrame(ledger) {
  for (const owner of ledger.owners) {
    if (!owner.disposed && owner.visible && owner.interactive &&
        ledger.frame.time - owner.lastTick <= 2 * ledger.intervalMs + DEADLINE_EPSILON_MS) return true;
  }
  return false;
}

function reservedVisibleKinds(ledger) {
  if (!ledger.frame) return 0;
  let count = 0;
  for (const owner of ledger.owners) {
    if (owner.disposed || !owner.visible) continue;
    const fresh = ledger.frame.time - owner.lastTick <= 2 * ledger.intervalMs + DEADLINE_EPSILON_MS;
    // Physical GPU work still occupies capacity when its canvas's callback is
    // late. Stale pending intent alone must not reserve capacity indefinitely.
    if (fresh && owner.pendingLatest || owner.inFlight?.kind === 'latest') count++;
    if (fresh && owner.pendingProgress || owner.inFlight?.kind === 'progress') count++;
  }
  return count;
}

function limits(ledger) {
  const interval = ledger.intervalMs;
  const interactive = interactiveFrame(ledger);
  // Settled refinement can use the existing 8 ms packet ceiling even on a
  // fast display. Leave capacity for composition and packet retirement;
  // interactive input restores the display-relative interactive allowance.
  const compute = Math.max(0, Math.min(interactive ? interval / 3 : Math.max(12, interval * 2 / 3),
    (interactive ? interval : Math.max(12, interval)) - (ledger.frame?.presentationMs || 0)));
  const reservedKinds = reservedVisibleKinds(ledger);
  const ordinarySubmission = interactive ? Math.min(4, interval / 4) : 8;
  return {
    interactive,
    // Every reserved kind needs one packet in two display intervals. Sharing
    // F/3 between two canvases cannot support four packets each sized F/4.
    // Reduce future quanta, preserving the grid and unfinished submissions.
    submission: Math.min(ordinarySubmission, reservedKinds
      ? 0.9 * 2 * compute / reservedKinds : ordinarySubmission),
    compute,
    reservedKinds,
    install: Math.min(2, interval / 8),
  };
}

function updateFrame(ledger, time, options) {
  let previous = ledger.frame;
  if (previous && time < previous.time - 1e-4) {
    // Test clocks, resumed browser clock domains, and injected clocks may begin
    // a new epoch. Preserve unfinished owner slots while translating waiting
    // clocks; otherwise an old timestamp can block every future admission.
    const offset = time - previous.time;
    for (const owner of ledger.owners) {
      for (const key of ['lastTick', 'waitingSince', 'lastAdmission', 'latestDeadline', 'progressDeadline']) {
        if (Number.isFinite(owner[key])) owner[key] += offset;
      }
    }
    ledger.clockEpoch++;
    ledger.intervalSamples.length = 0;
    ledger.frame = null;
    previous = null;
  }
  // RAF callbacks share a timestamp. The tolerance also permits callers using
  // performance.now() shortly after RAF without creating a second budget.
  if (previous && time - previous.time < ledger.intervalMs / 2 - 1e-4) return;
  let suppressCompute = false;
  if (previous) {
    const delta = time - previous.time;
    let wasInteractive = false;
    let hadVisibleWork = false;
    for (const owner of ledger.owners) {
      if (!owner.disposed && owner.visible && previous.time - owner.lastTick <= 2 * ledger.intervalMs) {
        wasInteractive ||= owner.interactive;
        hadVisibleWork ||= hasPending(owner);
      }
    }
    // Do not mistake an idle pause or a compute stall for a slower display.
    // The lower quartile tolerates occasional skipped idle frames while still
    // learning both 60 -> 120 Hz and 120 -> 60 Hz from a stable idle sequence.
    if (!wasInteractive && !options.interactive && previous.computeMs === 0 && boundedInterval(delta)) {
      ledger.intervalSamples.push(delta);
      if (ledger.intervalSamples.length > MAX_INTERVAL_SAMPLES) ledger.intervalSamples.shift();
      if (ledger.intervalSamples.length >= 5) {
        const sorted = [...ledger.intervalSamples].sort((a, b) => a - b);
        ledger.intervalMs = sorted[Math.floor((sorted.length - 1) / 4)];
      }
    }
    suppressCompute = hadVisibleWork && options.visible !== false && delta > 1.5 * ledger.intervalMs;
  }
  ledger.frame = {
    index: previous ? previous.index + 1 : 0,
    time,
    computeMs: 0,
    installMs: 0,
    presentationMs: 0,
    suppressCompute,
  };
}

function pendingKinds(options) {
  if (options.pending && typeof options.pending === 'object') {
    return { latest: !!options.pending.latest, progress: !!options.pending.progress };
  }
  return { latest: !!options.pending, progress: !!options.pending };
}

function kindDeadline(owner, kind) {
  return kind === 'progress' ? owner.progressDeadline : owner.latestDeadline;
}

function dueKind(owner, ledger) {
  const now = ledger.frame.time;
  const latest = owner.pendingLatest && owner.latestDeadline <= now + ledger.intervalMs + DEADLINE_EPSILON_MS;
  const progress = owner.pendingProgress && owner.progressDeadline <= now + ledger.intervalMs + DEADLINE_EPSILON_MS;
  if (latest && progress) return owner.latestDeadline <= owner.progressDeadline + DEADLINE_EPSILON_MS ? 'latest' : 'progress';
  return latest ? 'latest' : progress ? 'progress' : null;
}

function preferredOwner(ledger) {
  const now = ledger.frame.time;
  let selected = null;
  for (const owner of ledger.owners) {
    if (!isEligible(owner, ledger)) continue;
    const opportunityDeadline = owner.lastAdmission === null
      ? owner.waitingSince + 2 * ledger.intervalMs : owner.lastAdmission + 2 * ledger.intervalMs;
    // One kind receiving work does not satisfy the other kind's reservation.
    // Keep a canvas urgent until its older latest/progress obligation also gets
    // capacity, including when another focused canvas ticks earlier in RAF.
    const deadline = Math.min(opportunityDeadline,
      owner.pendingLatest ? owner.latestDeadline : Infinity,
      owner.pendingProgress ? owner.progressDeadline : Infinity);
    const urgent = deadline <= now + ledger.intervalMs + DEADLINE_EPSILON_MS;
    const sameDeadline = selected && Math.abs(deadline - selected.deadline) <= DEADLINE_EPSILON_MS;
    if (!selected || (urgent && !selected.urgent) ||
        (urgent === selected.urgent && (urgent ? deadline < selected.deadline - DEADLINE_EPSILON_MS : owner.active && !selected.owner.active)) ||
        (urgent === selected.urgent && sameDeadline && owner.active && !selected.owner.active) ||
        (urgent === selected.urgent && sameDeadline && owner.active === selected.owner.active && owner.sequence < selected.owner.sequence)) {
      selected = { owner, deadline, urgent };
    }
  }
  return selected;
}

/**
 * Optional work accounting, shared across canvas owners.
 *
 * tick(time, { visible, interactive, active, pending }) takes an RAF timestamp.
 * active means gesture focus; interactive means input is still being delivered.
 * pending may be { latest, progress } to enable only the reservations that exist.
 * Replacing a latest camera does not reset its original reservation deadline.
 * beginSubmission reserves an estimate immediately; retireSubmission reconciles
 * a valid GPU duration, or a conservative fence estimate explicitly marked as
 * such. Cancellation/release never frees an unfinished GPU slot.
 */
export function createRenderBudget(ownerId, { ledger = sharedLedger } = {}) {
  const owner = {
    ownerId,
    sequence: ledger.nextOwner++,
    visible: false,
    interactive: false,
    active: false,
    disposed: false,
    pendingLatest: false,
    pendingProgress: false,
    latestDeadline: null,
    progressDeadline: null,
    waitingSince: null,
    lastAdmission: null,
    lastTick: -Infinity,
    inFlight: null,
    latestCostMs: null,
    costSource: null,
    gpuOverruns: 0,
    missedFrames: 0,
    presentationFrameIndex: null,
    presentationEpoch: null,
    presentationCostMs: 0,
    presentationCostSource: null,
  };
  ledger.owners.add(owner);

  function snapshot() {
    const frame = ledger.frame;
    const budget = frame ? limits(ledger) : {
      submission: Math.min(4, ledger.intervalMs / 4), compute: ledger.intervalMs / 3,
      install: Math.min(2, ledger.intervalMs / 8), interactive: false, reservedKinds: 0,
    };
    return {
      frameIntervalMs: ledger.intervalMs,
      submissionBudgetMs: budget.submission,
      reservedVisibleKinds: budget.reservedKinds,
      remainingComputeMs: Math.max(0, budget.compute - (frame ? frame.computeMs : 0)),
      computeBudgetMs: budget.compute,
      installBudgetMs: budget.install,
      remainingInstallMs: Math.max(0, budget.install - (frame ? frame.installMs : 0)),
      dueKind: frame ? dueKind(owner, ledger) : null,
      latestDeadline: owner.latestDeadline,
      progressDeadline: owner.progressDeadline,
      suppressCompute: !!(frame && frame.suppressCompute),
      inFlight: !!owner.inFlight,
      latestCostMs: owner.latestCostMs,
      costSource: owner.costSource,
      gpuOverruns: owner.gpuOverruns,
      missedFrames: owner.missedFrames,
      presentationMs: frame?.presentationMs || 0,
      presentationCostMs: owner.presentationCostMs,
      presentationCostSource: owner.presentationCostSource,
    };
  }

  function tick(time, options = {}) {
    if (owner.disposed || !Number.isFinite(time)) return snapshot();
    const previousFrame = ledger.frame;
    updateFrame(ledger, time, options);
    updatePending(options.pending);
    owner.visible = options.visible !== false;
    owner.interactive = !!options.interactive;
    owner.active = !!options.active;
    owner.lastTick = time;
    if (previousFrame !== ledger.frame && ledger.frame.suppressCompute) owner.missedFrames++;
    return snapshot();
  }

  function updatePending(nextPending) {
    if (owner.disposed || !ledger.frame) return snapshot();
    const pending = pendingKinds({ pending: nextPending });
    const now = ledger.frame.time;
    if (!hasPending(owner) && (pending.latest || pending.progress)) {
      owner.waitingSince = now;
      owner.lastAdmission = null;
    }
    if (pending.latest && !owner.pendingLatest) owner.latestDeadline = now + 2 * ledger.intervalMs;
    if (pending.progress && !owner.pendingProgress) owner.progressDeadline = now + 2 * ledger.intervalMs;
    if (!pending.latest) owner.latestDeadline = null;
    if (!pending.progress) owner.progressDeadline = null;
    owner.pendingLatest = pending.latest;
    owner.pendingProgress = pending.progress;
    return snapshot();
  }

  function observePresentation(ms, { estimate = true } = {}) {
    if (!ledger.frame || owner.disposed || !owner.visible || !validDuration(ms)) return false;
    const sameFrame = owner.presentationFrameIndex === ledger.frame.index &&
      owner.presentationEpoch === ledger.clockEpoch;
    if (sameFrame && owner.presentationCostSource === 'gpu-timer' && estimate) return false;
    const previous = sameFrame ? owner.presentationCostMs : 0;
    ledger.frame.presentationMs = Math.max(0, ledger.frame.presentationMs + ms - previous);
    owner.presentationFrameIndex = ledger.frame.index;
    owner.presentationEpoch = ledger.clockEpoch;
    owner.presentationCostMs = ms;
    owner.presentationCostSource = estimate ? 'estimate' : 'gpu-timer';
    return true;
  }

  function canSubmit({ kind = 'latest', estimateMs } = {}) {
    if (!ledger.frame || !isEligible(owner, ledger) || ledger.frame.suppressCompute) return false;
    if ((kind === 'latest' && !owner.pendingLatest) || (kind === 'progress' && !owner.pendingProgress)) return false;
    const budget = limits(ledger);
    const cost = estimateMs === undefined ? budget.submission : estimateMs;
    if (!validDuration(cost) || cost > budget.submission || ledger.frame.computeMs + cost > budget.compute + 1e-9) return false;
    const due = dueKind(owner, ledger);
    if (due && due !== kind && kindDeadline(owner, due) < kindDeadline(owner, kind) - DEADLINE_EPSILON_MS) return false;
    const preferred = preferredOwner(ledger);
    return !preferred || preferred.owner === owner || (!preferred.urgent &&
      (!preferred.owner.active || preferred.owner.lastAdmission === ledger.frame.time));
  }

  function recordAdmission(kind) {
    const now = ledger.frame.time;
    owner.lastAdmission = now;
    if (kind === 'progress') owner.progressDeadline = now + 2 * ledger.intervalMs;
    else owner.latestDeadline = now + 2 * ledger.intervalMs;
  }

  function beginSubmission({ kind = 'latest', estimateMs } = {}) {
    if (!canSubmit({ kind, estimateMs })) return null;
    const cost = estimateMs === undefined ? limits(ledger).submission : estimateMs;
    const ticket = {
      id: ledger.nextTicket++, owner, kind, estimateMs: cost,
      frameIndex: ledger.frame.index, clockEpoch: ledger.clockEpoch,
      budgetMs: limits(ledger).submission, retired: false,
    };
    ledger.frame.computeMs += cost;
    owner.inFlight = ticket;
    recordAdmission(kind);
    return ticket;
  }

  function observeGpuCost(cost, { estimate = false, budgetMs } = {}) {
    if (!validDuration(cost)) return false;
    owner.latestCostMs = cost;
    owner.costSource = estimate ? 'fence-estimate' : 'gpu-timer';
    const allowed = budgetMs === undefined ? (ledger.frame ? limits(ledger).submission : Math.min(4, ledger.intervalMs / 4)) : budgetMs;
    if (cost > allowed) owner.gpuOverruns++;
    return true;
  }

  function retireSubmission(ticket, { gpuMs, fenceMs } = {}) {
    if (!ticket || ticket.owner !== owner || ticket.retired || owner.inFlight !== ticket) return false;
    const measured = validDuration(gpuMs) ? gpuMs : validDuration(fenceMs) ? fenceMs : ticket.estimateMs;
    const estimate = !validDuration(gpuMs);
    // A later frame cannot spend unused past capacity. Overruns remain charged
    // even when retirement happens after cancellation or a display interval.
    if (ledger.frame) {
      const adjustment = measured - ticket.estimateMs;
      if (ledger.clockEpoch === ticket.clockEpoch && ledger.frame.index === ticket.frameIndex) ledger.frame.computeMs = Math.max(0, ledger.frame.computeMs + adjustment);
      else if (adjustment > 0) ledger.frame.computeMs += adjustment;
    }
    observeGpuCost(measured, { estimate, budgetMs: ticket.budgetMs });
    ticket.retired = true;
    owner.inFlight = null;
    if (owner.disposed) {
      ledger.owners.delete(owner);
      if (ledger.owners.size === 0) ledger.frame = null;
    }
    return true;
  }

  function consumeCompute(ms, { kind = 'latest' } = {}) {
    if (!ledger.frame || !validDuration(ms)) return false;
    const withinBudget = canSubmit({ kind, estimateMs: ms });
    ledger.frame.computeMs += ms;
    recordAdmission(kind);
    return withinBudget;
  }

  function canInstall(estimateMs = 0) {
    return !!ledger.frame && !owner.disposed && owner.visible && validDuration(estimateMs) &&
      ledger.frame.installMs + estimateMs <= limits(ledger).install + 1e-9;
  }

  function consumeInstall(ms) {
    if (!ledger.frame || !validDuration(ms)) return false;
    const withinBudget = canInstall(ms);
    ledger.frame.installMs += ms;
    return withinBudget;
  }

  function release() {
    owner.visible = false;
    owner.active = false;
    owner.interactive = false;
    owner.pendingLatest = false;
    owner.pendingProgress = false;
    owner.latestDeadline = null;
    owner.progressDeadline = null;
    owner.waitingSince = null;
    owner.lastAdmission = null;
  }

  function dispose() {
    release();
    owner.disposed = true;
    if (!owner.inFlight) {
      ledger.owners.delete(owner);
      if (ledger.owners.size === 0) ledger.frame = null;
    }
  }

  return { tick, updatePending, canSubmit, beginSubmission, retireSubmission, consumeCompute,
    canInstall, consumeInstall, observeGpuCost, observePresentation, snapshot, release, dispose };
}
